import { computed, type ComputedRef } from 'vue';
import type { ElementProperties, ElementType } from './form.types';

const FONT_WEIGHT_MAP: Record<string, string> = {
  normal: 'normal',
  semibold: '600',
  bold: 'bold',
};

const LABEL_FONT_ELEMENTS = new Set<ElementType>([
  'TEXT_INPUT',
  'PARAGRAPH',
  'NUMBER_INPUT',
  'DATE_INPUT',
  'URL_INPUT',
  'DROPDOWN',
  'EMAIL_INPUT',
  'REGISTER_NUMBER_INPUT',
  'CHECKBOX',
  'RADIO_GROUP',
  'INDEXER_TITLE',
  'FILL_TITLE',
]);

const getFontSize = (fontSize: number | undefined, isLabelFontElement: boolean) => {
  return !isLabelFontElement && fontSize ? `${fontSize}px` : undefined;
};

const getFontWeight = (fontWeight: string | undefined) => {
  if (!fontWeight || fontWeight === 'normal') return undefined;
  return FONT_WEIGHT_MAP[fontWeight] || undefined;
};

const getFontStyle = (fontStyle: string | undefined) => {
  return fontStyle && fontStyle !== 'normal' ? fontStyle : undefined;
};

const getTextDecoration = (textDecoration: string | undefined) => {
  return textDecoration && textDecoration !== 'none' ? textDecoration : undefined;
};

const getSpacingValue = (value: number | undefined) => {
  return value ? `${value}px` : undefined;
};

const getWidth = (width: number | undefined) => {
  return width && width > 0 && width < 100 ? `${width}%` : undefined;
};

const getHeight = (height: number | undefined) => {
  return height && height > 0 ? `${height}px` : undefined;
};

const getPaddingStyles = (padding: ElementProperties['padding']) => {
  return {
    paddingTop: getSpacingValue(padding?.top),
    paddingRight: getSpacingValue(padding?.right),
    paddingBottom: getSpacingValue(padding?.bottom),
    paddingLeft: getSpacingValue(padding?.left),
  };
};

export function useElementContainerStyles(
  properties: ComputedRef<ElementProperties> | ElementProperties,
  elementType?: ElementType
) {
  const props = computed(() => {
    if (properties && typeof properties === 'object' && 'value' in properties) {
      return (properties as ComputedRef<ElementProperties>).value;
    }
    return properties as ElementProperties;
  });

  const containerStyles = computed(() => {
    const props_val = props.value;
    const isLabelFontElement = elementType ? LABEL_FONT_ELEMENTS.has(elementType) : false;

    return {
      width: getWidth(props_val.width),
      minHeight: getHeight(props_val.height),
      fontSize: getFontSize(props_val.fontSize, isLabelFontElement),
      fontWeight: isLabelFontElement ? undefined : getFontWeight(props_val.fontWeight),
      fontStyle: isLabelFontElement ? undefined : getFontStyle(props_val.fontStyle),
      textDecoration: isLabelFontElement ? undefined : getTextDecoration(props_val.textDecoration),
      textDecorationSkipInk: isLabelFontElement ? undefined : ('none' as const),
      textAlign: isLabelFontElement ? undefined : props_val.textAlign,
      color: isLabelFontElement ? undefined : props_val.color,
      backgroundColor: props_val.backgroundColor,
      ...getPaddingStyles(props_val.padding),
    };
  });

  return {
    containerStyles,
  };
}
